/**
 * G08/G16: Map Modal UI
 * Modal dialog wrapper for ParcelMap component with multi-parcel selection
 */

import './MapModal.css'
import { ParcelMap } from '../geodata/ParcelMap'
import type { ParcelResult } from '../geodata/types'
import { mergeParcels } from '../geodata/polygonMerge'
import { t } from '../i18n'

type TabType = 'address' | 'parcelId'

export class MapModal {
  private backdrop: HTMLElement | null = null
  private container: HTMLElement | null = null
  private parcelMap: ParcelMap | null = null
  private selectedParcels: ParcelResult[] = []
  private isAdjacent: boolean = true
  private activeTab: TabType = 'address'
  private isOpen: boolean = false

  // Callbacks
  public onImport: ((parcel: ParcelResult) => void) | null = null
  public onClose: (() => void) | null = null

  constructor() {
    // Bind methods
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  // ============ Lifecycle ============

  open(): void {
    if (this.isOpen) return
    this.isOpen = true

    // Create backdrop
    this.backdrop = document.createElement('div')
    this.backdrop.className = 'map-modal-backdrop'
    this.backdrop.addEventListener('click', () => this.close())

    // Create modal container
    this.container = document.createElement('div')
    this.container.className = 'map-modal'
    this.container.innerHTML = this.getModalHTML()

    // Prevent clicks inside modal from closing it
    this.container.addEventListener('click', (e) => e.stopPropagation())

    // Append to body
    document.body.appendChild(this.backdrop)
    document.body.appendChild(this.container)

    // Initialize ParcelMap after DOM is ready with a delay
    setTimeout(() => {
      const mapContainer = this.container?.querySelector('.map-modal-map') as HTMLElement
      if (mapContainer) {
        // Debug: log container size
        console.log('[MapModal] Map container size:', mapContainer.offsetWidth, 'x', mapContainer.offsetHeight)

        this.parcelMap = new ParcelMap(mapContainer, { zoom: 6 })
        this.parcelMap.onParcelSelected = this.handleParcelSelected.bind(this)
        this.parcelMap.onParcelsChanged = this.handleParcelsChanged.bind(this)
        this.parcelMap.onAdjacencyWarning = this.handleAdjacencyWarning.bind(this)
        this.parcelMap.onError = this.handleError.bind(this)
        this.parcelMap.onLoading = this.handleLoading.bind(this)

        // Enable click-to-select if address tab is active (switchTab ran before parcelMap was ready)
        if (this.activeTab === 'address') {
          this.parcelMap.enableClickToSelect()
        }

        // Fix Leaflet size after modal is visible (multiple attempts for reliability)
        setTimeout(() => {
          console.log('[MapModal] Calling invalidateSize')
          this.parcelMap?.invalidateSize()
        }, 100)
        setTimeout(() => this.parcelMap?.invalidateSize(), 300)
        setTimeout(() => this.parcelMap?.invalidateSize(), 500)
      } else {
        console.error('[MapModal] Map container not found!')
      }
    }, 50)

    // Setup event listeners
    this.setupEventListeners()

    // Set default tab
    this.switchTab('address')

    // Listen for escape key
    document.addEventListener('keydown', this.handleKeydown)
  }

  close(): void {
    if (!this.isOpen) return
    this.isOpen = false

    // Cleanup
    document.removeEventListener('keydown', this.handleKeydown)

    if (this.parcelMap) {
      this.parcelMap.destroy()
      this.parcelMap = null
    }

    if (this.backdrop) {
      this.backdrop.remove()
      this.backdrop = null
    }

    if (this.container) {
      this.container.remove()
      this.container = null
    }

    this.selectedParcels = []
    this.isAdjacent = true
    this.onClose?.()
  }

  destroy(): void {
    this.close()
  }

  // ============ Private Methods ============

  private getModalHTML(): string {
    return `
      <div class="map-modal-header">
        <h2 class="map-modal-title">${t('map.modal.title', 'Set Property Boundary')}</h2>
        <button class="map-modal-close" aria-label="Close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div class="map-modal-tabs">
        <button class="map-modal-tab active" data-tab="address">
          <i class="fa-solid fa-location-dot"></i>
          <span>${t('map.modal.tab.address', 'Address')}</span>
        </button>
        <button class="map-modal-tab" data-tab="parcelId">
          <i class="fa-solid fa-hashtag"></i>
          <span>${t('map.modal.tab.parcelId', 'Parcel ID')}</span>
        </button>
      </div>
      <div class="map-modal-input-area">
        <!-- Dynamic content based on tab -->
      </div>
      <div class="map-modal-map-container">
        <div class="map-modal-map"></div>
      </div>
      <div class="map-modal-info">
        <span class="map-modal-info-text">${t('map.modal.noSelection', 'No parcel selected')}</span>
      </div>
      <div class="map-modal-actions">
        <button class="map-modal-btn secondary" data-action="cancel">
          ${t('map.modal.cancel', 'Cancel')}
        </button>
        <button class="map-modal-btn primary" data-action="import" disabled>
          <i class="fa-solid fa-download"></i>
          ${t('map.modal.import', 'Import Boundary')}
        </button>
      </div>
    `
  }

  private setupEventListeners(): void {
    if (!this.container) return

    // Close button
    const closeBtn = this.container.querySelector('.map-modal-close')
    closeBtn?.addEventListener('click', () => this.close())

    // Tab buttons
    const tabs = this.container.querySelectorAll('.map-modal-tab')
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabType = tab.getAttribute('data-tab') as TabType
        this.switchTab(tabType)
      })
    })

    // Action buttons
    const cancelBtn = this.container.querySelector('[data-action="cancel"]')
    cancelBtn?.addEventListener('click', () => this.close())

    const importBtn = this.container.querySelector('[data-action="import"]')
    importBtn?.addEventListener('click', () => this.handleImport())

    // Search form submission
    this.container.addEventListener('submit', (e) => {
      e.preventDefault()
      this.handleSearch()
    })
  }

  private switchTab(tab: TabType): void {
    this.activeTab = tab

    if (!this.container) return

    // Update tab buttons
    const tabs = this.container.querySelectorAll('.map-modal-tab')
    tabs.forEach(t => {
      const isActive = t.getAttribute('data-tab') === tab
      t.classList.toggle('active', isActive)
    })

    // Update input area
    const inputArea = this.container.querySelector('.map-modal-input-area')
    if (!inputArea) return

    if (tab === 'address') {
      inputArea.innerHTML = `
        <form class="map-modal-search-form">
          <input
            type="text"
            class="map-modal-input"
            placeholder="${t('map.modal.placeholder.address', 'Enter address, e.g., Warszawa, Marszałkowska 1')}"
            autofocus
          >
          <button type="submit" class="map-modal-search-btn">
            <i class="fa-solid fa-search"></i>
            ${t('map.modal.search', 'Search')}
          </button>
        </form>
        <p class="map-modal-browse-hint map-modal-browse-hint--subtle">
          <i class="fa-solid fa-mouse-pointer"></i>
          ${t('map.modal.browseHint', 'Click on the map to select a parcel')}
        </p>
      `
      this.parcelMap?.enableClickToSelect()
    } else {
      inputArea.innerHTML = `
        <form class="map-modal-search-form">
          <input
            type="text"
            class="map-modal-input"
            placeholder="${t('map.modal.placeholder.parcelId', 'Enter parcel ID, e.g., 146510_8.0310.60')}"
            autofocus
          >
          <button type="submit" class="map-modal-search-btn">
            <i class="fa-solid fa-search"></i>
            ${t('map.modal.search', 'Search')}
          </button>
        </form>
      `
      this.parcelMap?.disableClickToSelect()
    }
  }

  private handleSearch(): void {
    const input = this.container?.querySelector('.map-modal-input') as HTMLInputElement
    if (!input || !input.value.trim()) return

    const value = input.value.trim()

    if (this.activeTab === 'address') {
      this.parcelMap?.searchByAddress(value)
    } else if (this.activeTab === 'parcelId') {
      this.parcelMap?.searchByParcelId(value)
    }
  }

  private handleParcelSelected(_parcel: ParcelResult): void {
    this.showToast('success', t('map.modal.parcelFound', 'Parcel found'))
  }

  private handleParcelsChanged(parcels: ParcelResult[]): void {
    this.selectedParcels = parcels
    this.updateInfoBar()
    this.updateImportButton()
  }

  private handleAdjacencyWarning(isAdjacent: boolean): void {
    this.isAdjacent = isAdjacent
    this.updateInfoBar()
  }

  private handleError(error: Error): void {
    this.showToast('error', error.message)
    this.updateInfoBar(error.message)
  }

  private handleLoading(loading: boolean): void {
    const importBtn = this.container?.querySelector('[data-action="import"]') as HTMLButtonElement
    if (importBtn && loading) {
      importBtn.disabled = true
    }
  }

  private handleImport(): void {
    if (this.selectedParcels.length === 0) return

    let parcelToImport: ParcelResult
    if (this.selectedParcels.length === 1) {
      parcelToImport = this.selectedParcels[0]
    } else {
      parcelToImport = mergeParcels(this.selectedParcels)
    }

    this.onImport?.(parcelToImport)
    this.close()
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.close()
    }
  }

  private updateInfoBar(errorMessage?: string): void {
    const infoText = this.container?.querySelector('.map-modal-info-text')
    if (!infoText) return

    if (errorMessage) {
      infoText.innerHTML = `<span class="error">${errorMessage}</span>`
    } else if (this.selectedParcels.length > 1) {
      const totalArea = this.selectedParcels.reduce((sum, p) => sum + p.area, 0)
      const area = totalArea.toLocaleString('pl-PL', { maximumFractionDigits: 0 })
      const count = this.selectedParcels.length
      const adjacencyWarning = !this.isAdjacent
        ? `<span class="separator">|</span><span class="warning"><i class="fa-solid fa-triangle-exclamation"></i> ${t('map.modal.adjacencyWarning', 'Selected parcels are not adjacent')}</span>`
        : ''
      infoText.innerHTML = `
        <strong>${t('map.modal.parcelsSelected', '{count} parcels selected').replace('{count}', String(count))}</strong>
        <span class="separator">|</span>
        <span>${area} m²</span>
        ${adjacencyWarning}
        <span class="separator">|</span>
        <a href="#" class="map-modal-clear-all">${t('map.modal.clearAll', 'Clear all')}</a>
      `
      // Bind clear all handler
      const clearLink = infoText.querySelector('.map-modal-clear-all')
      clearLink?.addEventListener('click', (e) => {
        e.preventDefault()
        this.parcelMap?.clearSelection()
        this.selectedParcels = []
        this.isAdjacent = true
        this.updateInfoBar()
        this.updateImportButton()
      })
    } else if (this.selectedParcels.length === 1) {
      const parcel = this.selectedParcels[0]
      const area = parcel.area.toLocaleString('pl-PL', { maximumFractionDigits: 0 })
      const region = `${parcel.region.commune}, ${parcel.region.county}`
      infoText.innerHTML = `
        <strong>${parcel.id}</strong>
        <span class="separator">|</span>
        <span>${area} m²</span>
        <span class="separator">|</span>
        <span>${region}</span>
      `
    } else {
      infoText.textContent = t('map.modal.noSelection', 'No parcel selected')
    }
  }

  private updateImportButton(): void {
    const importBtn = this.container?.querySelector('[data-action="import"]') as HTMLButtonElement
    if (!importBtn) return

    const count = this.selectedParcels.length
    importBtn.disabled = count === 0

    // Update button text
    const icon = importBtn.querySelector('i')
    const iconHtml = icon ? icon.outerHTML + ' ' : ''
    if (count > 1) {
      importBtn.innerHTML = iconHtml + t('map.modal.importParcels', 'Import {count} Parcels').replace('{count}', String(count))
    } else {
      importBtn.innerHTML = iconHtml + t('map.modal.import', 'Import Boundary')
    }
  }

  private showToast(type: 'success' | 'error', message: string): void {
    // Remove existing toasts
    this.container?.querySelectorAll('.map-modal-toast').forEach(t => t.remove())

    const toast = document.createElement('div')
    toast.className = `map-modal-toast ${type}`
    toast.innerHTML = `
      <i class="fa-solid ${type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}"></i>
      <span>${message}</span>
    `

    const mapContainer = this.container?.querySelector('.map-modal-map-container')
    mapContainer?.appendChild(toast)

    // Auto-dismiss after 5 seconds
    setTimeout(() => toast.remove(), 5000)
  }
}
